/* eslint-disable no-console */
import { LogLevelType } from '@shared/content/types';

class ConsoleLogger {
  #prefix = '[-]';

  #logLevelsEnum: { [key: string]: LogLevelType[] } = {
    silent: [],
    error: ['error'],
    warn: ['error', 'warn'],
    info: ['error', 'warn', 'info'],
    verbose: ['error', 'warn', 'info', 'verbose'],
  };

  #defaultLogLevel: LogLevelType = 'warn';

  constructor() {
    window.Utiq ||= {};
    window.Utiq.currentLogLevel ||= this.#defaultLogLevel;
  }

  get prefix() {
    return this.#prefix;
  }

  get defaultLogLevel() {
    return this.#defaultLogLevel;
  }

  get logLevel() {
    return window.Utiq?.currentLogLevel || this.#defaultLogLevel;
  }

  get logLevels() {
    return this.#logLevelsEnum;
  }

  setLogLevel(logLevel: LogLevelType) {
    if (logLevel in this.#logLevelsEnum) {
      window.Utiq ||= {};
      window.Utiq.currentLogLevel = logLevel;
    }
  }

  initialize(prefix: string, logLevel?: LogLevelType) {
    this.#prefix = `[${prefix}]:`;
    if (logLevel) this.setLogLevel(logLevel);
  }

  #isLogLevelAllowed(logLevel: LogLevelType) {
    return this.#logLevelsEnum[this.logLevel].includes(logLevel);
  }

  /* Handled (catched) exceptions and formatted unhandled exceptions - always logged */
  logError(...logs: unknown[]) {
    if (this.#isLogLevelAllowed('error')) {
      console.error(this.#prefix, ...logs);
    }
  }

  /* Non-breaking failures, attention grabbers, deprecation warnings */
  logWarn(...logs: unknown[]) {
    if (this.#isLogLevelAllowed('warn')) {
      console.warn(this.#prefix, ...logs);
    }
  }

  /* IT-level information, starting/stopping services, general information */
  logInfo(...logs: unknown[]) {
    if (this.#isLogLevelAllowed('info')) {
      console.info(this.#prefix, '[INFO]', ...logs);
    }
  }

  /* Variables values, internal flows, functions outputs, significant conditions checking outputs */
  logDebug(...logs: unknown[]) {
    if (this.#isLogLevelAllowed('verbose')) {
      console.info(this.#prefix, '[DEBUG]', ...logs);
    }
  }

  /* Requests, responses, 3rd party calls, flow calls */
  logTrace(...logs: unknown[]) {
    if (this.#isLogLevelAllowed('verbose')) {
      console.info(this.#prefix, '[TRACE]', ...logs);
    }
  }
}

/* This assures having only 1 instance per module, as the object is created on the first call and later the same one is returned on consecutive ones */
export const consoleLogger = new ConsoleLogger();
